import React, { act, FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import { Button, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { Badge, Drawer, Table, DatePicker, Progress, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import ActivityCreation from "./ActivityCreation";
import { useDispatch } from "react-redux";
import { updateActivityInDetailModalSlice } from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import { updatePhaseInDetailModalSlice } from "../../../store/slices/projectManagement/phase-view/PhaseInDetailView";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useWbsRecordName } from "../shared/useWbsCodes";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import FormSelect from "../../form-components/SelectComponent";
import { activityApis } from "../../../api/Activity";
import dayjs from "dayjs";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
import { projectsApis } from "../../../api/projects";
import { useLocation, useNavigate } from "react-router";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import { useAccessControl } from "../../../utils/useAccessControl";
const { RangePicker } = DatePicker;

const ActivityListView: FC<{
  currentTab: string;
  setHideTabTitles?: (hide: boolean) => void;
}> = ({ setHideTabTitles, currentTab }) => {
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { extra } = location.state || {};
  const { data: PhaseInDetailModal } = useSelector(
    (state: RootState) => state.PhaseInDetailModal
  );

  const {
    open,
    phaseName,
    extra: phaseExtra,
    allowEdit,
    allowAdd,
    projectResponse: {
      status: projectStatus,
      id: projectId,
      name: projectName,
    },
    id: phaseId,
  } = PhaseInDetailModal;
  const { status: phaseStatus } = phaseExtra || {};

  const [activityCreationModal, setActivityCreationModal] = useState(false);
  const [filtersModal, setFiltersModal] = useState(false);
  const [tagFilterData, setTagFilterData] = useState([]);
  const [ownerFilterData, setOwnerFilterData] = useState([]);
  const [statusFilterData, setStatusFilterData] = useState([]);
  const [customDateRange, setCustomDateRange] = useState<any>();

  // Get access control permissions
  const { canView } = useAccessControl();

  const columns: any = [
    {
      title: "Activity",
      dataIndex: "activityName",
      key: "activityName",
      width: 275,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`activity-${record?.activityId}`}
              onClick={() => {
                navigate(`/pms/project/activity-detail/${record.activityId}`, {
                  state: {
                    ...location.state,
                    projectId,
                    projectName,
                    phaseId,
                    phaseName,
                    activityId: record.activityId,
                    activityName: text,
                    activityStatus: record?.status,
                  }
                });
              }}
            >
              {activityLabel(record)}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`activity-${record?.activityId}`}
              direction="right"
              text={"View " + activityLabel(record) + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Planned Start Date",
      dataIndex: "activityStartDate",
      key: "activityStartDate",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Planned End Date",
      dataIndex: "activityEndDate",
      key: "activityEndDate",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Owner",
      dataIndex: "activityOwnerName",
      key: "activityOwnerName",
      width: 200,
    },
    {
      title: "Total Tasks",
      dataIndex: "totalTasks",
      key: "totalTasks",
      width: 200,
    },
    {
      title: "Completed Tasks",
      dataIndex: "completedTasks",
      key: "completedTasks",
      width: 200,
    },
    {
      title: "Tasks Completion %",
      dataIndex: "tasksCompletionPercentage",
      key: "tasksCompletionPercentage",
      width: 220,
      render: (data: any) => {
        const percent = Number(data);
        if (data === null || data === undefined || isNaN(percent)) return "N/A";
        return (
          <Tooltip title={`${Math.round(percent)}% Complete`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percent}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Budget Progress",
      dataIndex: "budgetConsumption",
      key: "budgetConsumption",
      width: 200,
      render: (data: any, record: any) => {
        const consumed = Number(data) || 0;
        const planned = Number(record?.budgetPlanned) || 0;
        const fmt = (n: number) =>
          `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
        if (planned <= 0) return consumed > 0 ? fmt(consumed) : "N/A";
        const percent = Math.round((consumed / planned) * 100);
        return (
          <Tooltip
            title={`${fmt(consumed)} of ${fmt(planned)} (${percent}%)`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={Math.min(100, percent)}
              status={percent > 100 ? "exception" : "active"}
              strokeColor={
                percent > 100
                  ? "#ff4d4f"
                  : { from: "#1677ff", to: "#1677ff" }
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "CreatedAt",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 200,
      fixed: "right",
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const {
    formState: { errors, dirtyFields },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  const activityLabel = useWbsRecordName(
    dataSource,
    "activityId",
    "activityName"
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    defaultCurrent: 1,
    defaultPageSize: 15,
    showSizeChanger: true,
    pageSizeOptions: ["15", "20", "30", "40", "50"],
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);
  const [filterCount, setFilterCount] = useState(0);
  const [endOfList, setEndOfList] = useState(false);
  const [filteredData, setFilteredData] = useState<any>({});

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
      } = params;
      const apiResponse: any = await activityApis.fetchActivityList({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        projectIds: projectId?.toString(),
        phaseIds: phaseId?.toString(),
        tagIds: filteredData?.tag?.join(",") || null,
        activityStatus: filteredData?.status || null,
        activityOwnerIds: filteredData?.resourceHead?.join(",") || null,
        startDate:
          (customDateRange && dayjs(customDateRange[0]).format("YYYY-MM-DD")) ||
          null,
        endDate:
          (customDateRange && dayjs(customDateRange[1]).format("YYYY-MM-DD")) ||
          null,
        completionPercentage: filteredData?.completion || null,
        derivedProjectStatus: extra?.screenName,
      });

      const { data, status } = apiResponse;
      if (status) {
        const { list, count, projectResponse, phaseResponse } = data || {};
        let _allowEdit = true;
        const {
          status: projectStatus,
          isLocked,
          lockedBy,
        } = projectResponse || {};
        const { status: phaseStatus } = phaseResponse || {};
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          // checking for project status
          _allowEdit = false;
        if (isLocked && lockedBy != currentUser?.id) {
          _allowEdit = false;
        } // checking for project lock
        if (
          ["closed", "onhold", "archived"]?.includes(phaseStatus?.toLowerCase())
        )
          // checking for phase status
          _allowEdit = false;

        // Separate permission for Add Activity (same as Phases page)
        let _allowAdd = true;
        if (
          ["closed", "onhold", "archived"]?.includes(
            projectStatus?.toLowerCase()
          )
        )
          _allowAdd = false;
        if (isLocked && lockedBy != currentUser?.id) _allowAdd = false;
        if (
          ["closed", "onhold", "archived"]?.includes(phaseStatus?.toLowerCase())
        )
          _allowAdd = false;

        dispatch(
          updateActivityInDetailModalSlice({
            projectResponse: {
              ...projectResponse,
              name: projectName,
            },
            phaseResponse: {
              ...phaseResponse,
              name: phaseName,
            },
            allowEdit: _allowEdit,
          })
        );
        dispatch(
          updatePhaseInDetailModalSlice({
            allowAdd: _allowAdd,
          })
        );

        if (list?.length < pageSize) {
          setEndOfList(true);
        }
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize !== pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current !== pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        // Extract paginationId from dataSource - API response has uid field (used as rowKey)
        // Similar to TaskListView which uses taskId
        apiParams.paginationId =
          apiParams.direction === "F"
            ? dataSource[dataSource.length - 1]?.uid || // Primary: uid field from API response (for forward - page 2, 3, etc.)
              dataSource[dataSource.length - 1]?.paginationId ||
              dataSource[dataSource.length - 1]?.activityId
            : dataSource[0]?.uid || // Primary: uid field from API response (for backward - going back)
              dataSource[0]?.paginationId ||
              dataSource[0]?.activityId;

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: Number(newPagination.current) || pagination.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = () => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 15,
        defaultCurrent: 1,
        defaultPageSize: 15,
        showSizeChanger: true,
        pageSizeOptions: ["15", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    if (activityCreationModal) {
      setHideTabTitles?.(false);
      setActivityCreationModal(false);
    }
    if (open && currentTab === "activities") {
      refreshListView();
    }
  }, [phaseId, open]);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: (overRideData?.current || pagination.current).toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff:
        overRideData?.pageDiff !== undefined
          ? overRideData.pageDiff
          : pagination.current - 1,
      paginationId: null,
      searchQuery:
        overRideData?.searchQuery !== undefined
          ? overRideData.searchQuery
          : searchQuery,
      dateRange: "Quarter to Date",
      // customDateRange,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const fetchTagFilterData = async () => {
    try {
      const response = await activityApis.getTagsFilterData(
        projectId?.toString(),
        phaseId?.toString()
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.tagName,
            value: item.tagId,
          };
        });
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await activityApis.getOwnersFilterData(
        projectId?.toString(),
        phaseId?.toString()
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
          };
        });
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const fetchStatusFilterData = async () => {
    try {
      const response = await projectsApis.fetchStatusFiltersByModule(
        "Activity"
      );
      const { data, status } = response;
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
        setStatusFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    if (filtersModal) {
      fetchTagFilterData();
      fetchOwnerFilterData();
      fetchStatusFilterData();
    }
  }, [filtersModal]);

  useEffect(() => {
    if (Object?.keys(filteredData)?.length > 0) {
      refreshListView();
    }
  }, [filteredData]);

  if (activityCreationModal)
    return (
      <ActivityCreation
        modalOpen={activityCreationModal}
        setModalOpen={(open: boolean) => {
          setHideTabTitles?.(false);
          setActivityCreationModal(open);
        }}
        refreshData={refreshData}
      />
    );

  return (
    <>
      {canView("Activities") && (
        <FormProvider {...methods}>
          <form>
            <div
              className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
              style={{ minHeight: "40px", border: "0px" }}
            >
              <div className="nk-ibx-head-actions w-45 ms-1">
                <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                  <div className={`w-100 m-0 `}>
                    <SearchInput
                      name="search"
                      placeholder="Search..."
                      width="100%"
                      prefixIcon={<SearchOutlined />}
                      onDebouncedSearch={async (value: any) => {
                        setsearchQuery(value);
                        setPagination({
                          ...pagination,
                          current: 1,
                        });
                        await fetchData({
                          oldPage: 1,
                          current: "1",
                          sortOrder: "DESC",
                          pageSize: pagination.pageSize.toString(),
                          direction: "P",
                          pageDiff: 0,
                          paginationId: null,
                          searchQuery: value,
                        });
                      }}
                      debounceDelay={300} // Adjust delay as needed
                    />
                  </div>
                </ul>
              </div>
              <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                <li className="pt-0 pb-0">
                  <AddAccess moduleName="Activities">
                    <Button
                      className="toggle"
                      color="primary"
                      size="sm"
                      disabled={!allowAdd}
                      onClick={() => {
                        setHideTabTitles?.(true);
                        setActivityCreationModal(true);
                      }}
                    >
                      <Icon name={"plus"} />
                      <span>Add Activity</span>
                    </Button>
                  </AddAccess>
                </li>
                <li>
                  <UncontrolledDropdown>
                    <DropdownToggle
                      tag="a"
                      className="dropdown-toggle btn btn-icon btn-trigger"
                      onClick={(ev) => {
                        refreshListView();
                      }}
                    >
                      <Icon name="redo"></Icon>
                    </DropdownToggle>
                  </UncontrolledDropdown>
                </li>
                <li className="me-n1">
                  <a
                    href="#filter"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setFiltersModal(true);
                    }}
                    className={`btn-icon me-2`}
                  >
                    <Badge color="geekblue" size="small" count={filterCount}>
                      <Icon style={{ fontSize: "20px" }} name="filter"></Icon>
                    </Badge>
                  </a>
                </li>
              </ul>
            </div>
          </form>
        </FormProvider>
      )}

      {canView("Activities") ? (
        <div className="card-inner card-inner-lg p-0">
          <div className="content clearfix">
            <div
              style={{
                width: screenSize - 120 + "px",
                margin: "10px auto",
              }}
            >
              <SkeletonTable
                loading={loading}
                columns={columns as SkeletonTableColumnsType[]}
              >
                <Table
                  rowKey="uid"
                  columns={columns}
                  dataSource={dataSource}
                  pagination={pagination}
                  onChange={tableOnChange}
                  bordered
                  size="middle"
                  scroll={{
                    x: 1100,
                  }}
                />
              </SkeletonTable>
            </div>
          </div>
        </div>
      ) : (
        <div className="card-inner card-inner-lg p-0">
          <div className="content clearfix">
            <div
              style={{
                width: screenSize - 120 + "px",
                margin: "10px auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "400px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <h5
                  style={{
                    color: "#666",
                    marginBottom: "16px",
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  No View Access
                </h5>
                <p
                  style={{
                    color: "#999",
                    fontSize: "14px",
                    fontFamily: '"Roboto", sans-serif',
                  }}
                >
                  You don't have permission to view activities for this project.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Filters</strong>
            <>
              <div style={{ textAlign: "right" }}>
                <Button
                  onClick={() => {
                    methods.reset();
                    setFilterCount(0);
                    setFilteredData({});
                    refreshListView();
                  }}
                  color=""
                  className="m-3 mt-0 mb-0 ps-4 pe-4"
                  size="sm"
                >
                  <span style={{ color: "#0772f8" }}>Reset</span>
                </Button>
              </div>
            </>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => {
          setFiltersModal(false);
        }}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                setFilteredData(data);
                setFiltersModal(false);
                setFilterCount(0);
                // Count how many fields were actually changed (dirty)
                const dirtyData = getDirtyFieldsOnly(data, dirtyFields);
                let count =
                  dirtyData !== undefined ? Object.keys(dirtyData).length : 0;
                if (
                  customDateRange &&
                  customDateRange[0] &&
                  customDateRange[1]
                ) {
                  count += 1;
                }
                setFilterCount(count);
              })}
              className="w-100"
            >
              <div key={"filters5"} className="mt-0 mb-2">
                <label className="form-label">Select Date Range</label>
                <RangePicker
                  name={`dateRange`}
                  value={
                    customDateRange
                      ? [
                          dayjs(customDateRange[0].toDate()),
                          dayjs(customDateRange[1].toDate()),
                        ]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setCustomDateRange([dates[0], dates[1]]);
                    } else {
                      setCustomDateRange(null);
                    }
                  }}
                  style={{ width: "100%" }}
                />
              </div>
              <div key={"filters1"} className="mb-2 mt-0">
                <FormMultiSelect
                  name={`resourceHead`}
                  label={"Owner"}
                  placeholder={"By Resource Head"}
                  options={ownerFilterData}
                />
              </div>

              <div key={"filters2"} className="mt-0 mb-2">
                <FormMultiSelect
                  name={`tag`}
                  label={"Tag"}
                  placeholder={"By Tag"}
                  options={tagFilterData}
                />
              </div>

              <div key={"filters3"} className="mt-0 mb-2">
                <FormSelect
                  name={`completion`}
                  label={"Completion %"}
                  placeholder={"By Completion %"}
                  options={Array.from({ length: 20 }, (_, i) => {
                    const start = i * 5 + 1;
                    const end = (i + 1) * 5;
                    return {
                      value: `${start}-${end}`,
                      label: `${start}–${end}%`,
                    };
                  })}
                />
              </div>

              <div key={"filters4"} className="mt-0 mb-2">
                <FormSelect
                  name={`status`}
                  label={"Status"}
                  placeholder={"By Status"}
                  options={statusFilterData}
                />
              </div>

              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Find
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => {
                        methods.reset();
                        setFiltersModal(false);
                        setFilteredData({});
                        setFilterCount(0);
                      }}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>
    </>
  );
};

export default ActivityListView;
